import { defineMessages, type IntlShape } from 'react-intl';

export type TimeTrackingFieldValue = {
	originalEstimate?: string | null;
	remainingEstimate?: string | null;
};

const messages = defineMessages({
	originalEstimate: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.remaining-time-estimate-field.original-estimate',
		defaultMessage: 'Set original time estimate to {value}',
		description: 'Label for the original estimate in the remaining time estimate field',
	},
	remainingEstimate: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.remaining-time-estimate-field.remaining-estimate',
		defaultMessage: 'Set remaining time estimate to {value}',
		description: 'Label for the remaining estimate in the remaining time estimate field',
	},
	originalAndRemainingEstimate: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.remaining-time-estimate-field.original-and-remaining-estimate',
		defaultMessage:
			'Set original time estimate to {originalEstimate}, remaining to {remainingEstimate}',
		description:
			'Label for the original and remaining estimate in the remaining time estimate field',
	},
	clearOriginalAndRemainingEstimate: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.remaining-time-estimate-field.clear-original-and-remaining-estimate',
		defaultMessage: 'Clear original and remaining time estimates',
		description:
			'Label for the clear original and remaining estimate action in the remaining time estimate field',
	},
});

function isTimeTrackingField(value: unknown): value is TimeTrackingFieldValue {
	return (
		typeof value === 'object' &&
		value !== null &&
		('originalEstimate' in value || 'remainingEstimate' in value)
	);
}

export function toTimeTrackingFieldValue(value: unknown): TimeTrackingFieldValue | undefined {
	return isTimeTrackingField(value) ? value : undefined;
}

export function getFieldTextCopy({
	suggestedOriginalEstimate,
	suggestedRemainingEstimate,
	normalizedOriginalValue,
	normalizedRemainingValue,
	normalizedCurrentOriginalValue,
	normalizedCurrentRemainingValue,
	formatMessage,
}: {
	suggestedOriginalEstimate: string | null | undefined;
	suggestedRemainingEstimate: string | null | undefined;
	normalizedOriginalValue: string | null | undefined;
	normalizedRemainingValue: string | null | undefined;
	normalizedCurrentOriginalValue: string | null;
	normalizedCurrentRemainingValue: string | null;
	formatMessage: IntlShape['formatMessage'];
}): string | null {
	// Note that if a value is empty, it indicates that the estimate is being removed/cleared. If a value is undefined,
	// it indicates that the estimate is not being set/changed.

	const originalCleared = suggestedOriginalEstimate === '' || suggestedOriginalEstimate === null;
	const remainingCleared = suggestedRemainingEstimate === '' || suggestedRemainingEstimate === null;

	// Both estimates are present, so display copy text for both estimates
	if (suggestedOriginalEstimate && suggestedRemainingEstimate) {
		return formatMessage(messages.originalAndRemainingEstimate, {
			originalEstimate: normalizedOriginalValue,
			remainingEstimate: normalizedRemainingValue,
		});
	}

	// One estimate present, the other is undefined (i.e is not in the object), so only
	// display the estimate copy text.
	if (suggestedOriginalEstimate && suggestedRemainingEstimate === undefined) {
		return formatMessage(messages.originalEstimate, {
			value: normalizedOriginalValue,
		});
	}
	if (suggestedRemainingEstimate && suggestedOriginalEstimate === undefined) {
		return formatMessage(messages.remainingEstimate, {
			value: normalizedRemainingValue,
		});
	}

	// One estimate present, the other is empty (i.e is cleared), the cleared estimate gets set
	// to the new estimate's value.
	if (suggestedOriginalEstimate && remainingCleared) {
		return formatMessage(messages.originalAndRemainingEstimate, {
			originalEstimate: normalizedOriginalValue,
			remainingEstimate: normalizedOriginalValue,
		});
	}
	if (suggestedRemainingEstimate && originalCleared) {
		return formatMessage(messages.originalAndRemainingEstimate, {
			originalEstimate: normalizedRemainingValue,
			remainingEstimate: normalizedRemainingValue,
		});
	}

	// One estimate is empty (i.e is cleared) and the other is undefined (i.e is not in the object), the cleared
	// estimate gets set to the other estimate's value.
	if (originalCleared && suggestedRemainingEstimate === undefined) {
		return formatMessage(messages.originalEstimate, {
			value: normalizedCurrentRemainingValue,
		});
	}
	if (suggestedOriginalEstimate === undefined && remainingCleared) {
		return formatMessage(messages.remainingEstimate, {
			value: normalizedCurrentOriginalValue,
		});
	}

	// Both estimates are empty (i.e both are cleared), so display the cleared copy text.
	if (originalCleared && remainingCleared) {
		return formatMessage(messages.clearOriginalAndRemainingEstimate);
	}

	// No estimates are present, so return null.
	return null;
}
